import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import CustomerAddEdit from "./CustomerAddEdit"
import { CustomerHeader } from "./CustomerHeader"
import PageWrapper from "components/Common/PageWrapper"
import { useCustomers, useDeleteCustomer } from "hooks/useCustomer"
import { useNavigate } from "react-router-dom"

const CustomerList = () => {
  document.title = "Customer List"

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fetchedData, setFetchedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // URL search params hook
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  // Hooks
  const navigate = useNavigate()

  // Query params
  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  // React Query hooks
  const {
    data: fetchedResponseData,
    isLoading,
    refetch: refetchResponseData,
    isRefetching,
  } = useCustomers(queryParams)

  const { mutateAsync: deleteFetchedData, isPending: deleteLoading } =
    useDeleteCustomer()

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setFetchedData(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFetchedData(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setFetchedData(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleViewClick = useCallback(item => {
    navigate(`/customer-list/${item?.cuid}/view`)
  }, [])

  const handleDeleteClick = useCallback(item => {
    setFetchedData(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!fetchedData?.id) return
    try {
      const res = await deleteFetchedData(fetchedData.id)
      toast.success(res?.message ?? "Customer deleted successfully.")
      setDeleteModal(false)
      refetchResponseData()
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete customer.")
    }
  }, [fetchedData, deleteFetchedData, refetchResponseData])

  const handleRefresh = () => refetchResponseData()

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Basic Info",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div>
              <div>
                <i className="bx bx-id-card"></i> <strong>ID:</strong>{" "}
                {data.cuid}
              </div>
              <div>
                <i className="bx bx-user"></i> <strong>Name:</strong>{" "}
                {data.name}
              </div>
              <div>
                <i className="bx bx-phone"></i> <strong>Mobile:</strong>{" "}
                {data.mobile}
              </div>
              <div>
                <i className="bx bx-envelope"></i> <strong>Email:</strong>{" "}
                {data.email}
              </div>
            </div>
          )
        },
      },
      {
        header: "Work Info",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div>
              <div>
                <i className="bx bx-briefcase"></i>{" "}
                <strong>Designation:</strong> {data.designation?.name || "-"}
              </div>
              <div>
                <i className="bx bx-building"></i> <strong>Department:</strong>{" "}
                {data.department?.name || "-"}
              </div>
              <div>
                <i className="bx bx-home-circle"></i> <strong>Company:</strong>{" "}
                {data.company?.name || "-"}
              </div>
              <div>
                <i className="bx bx-briefcase-alt-2"></i>{" "}
                <strong>Customer Type:</strong>{" "}
                {data.customer_type?.name || "-"}
              </div>
            </div>
          )
        },
      },
      {
        header: "Location Info",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div>
              <div>
                <i className="bx bx-map"></i> <strong>Area:</strong>{" "}
                {data.area?.name || "-"}
              </div>
              <div>
                <i className="bx bx-home-alt"></i> <strong>City:</strong>{" "}
                {data.city?.name || "-"}
              </div>
              <div>
                <i className="bx bx-location-plus"></i>{" "}
                <strong>Full Address:</strong> {data.full_address || "-"}
              </div>
            </div>
          )
        },
      },
      {
        header: "Meta Info",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div>
              <div>
                <i className="bx bx-calendar"></i> <strong>Created:</strong>{" "}
                {data.created_at ? formatReadableDate(data.created_at) : "-"}
              </div>
              <div>
                <i className="bx bx-cog"></i> <strong>Action:</strong>{" "}
                <span className="d-flex gap-1">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleViewClick(data)}
                    id={`viewtooltip-${data.cuid}`}
                  >
                    <i className="mdi mdi-eye" />
                  </Button>
                  <UncontrolledTooltip
                    placement="top"
                    target={`viewtooltip-${data.cuid}`}
                  >
                    View
                  </UncontrolledTooltip>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEditClick(data)}
                    id={`edittooltip-${data.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip
                    placement="top"
                    target={`edittooltip-${data.id}`}
                  >
                    Edit
                  </UncontrolledTooltip>

                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(data)}
                    id={`deletetooltip-${data.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip-${data.id}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </span>
              </div>
            </div>
          )
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = fetchedResponseData?.data?.data || []
  const paginationInfo = fetchedResponseData?.data

  return (
    <PageWrapper title="Customer List" breadcrumbItem="Customers">
      <CustomerHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <CustomerAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={fetchedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default CustomerList
